import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@apollo/client';
import { myCreatorHasCampaignsForManagerDocument } from '@social-garden/api/documents/creatorHasCampaign.ts';
import {
  Card,
  CardContent,
  Container,
  FormControl,
  Grid,
  InputLabel,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';
import { CreatorHasCampaignStatus } from '@social-garden/api/gql/graphql.ts';
import { faHandshake } from '@fortawesome/pro-thin-svg-icons/faHandshake';
import { ChangeEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import CreatorHasCampaignStatusSelect from '@/components/CreatorHasCampaignStatusSelect.tsx';
import StackList from '@/components/StackList.tsx';
import CreatorHasCampaignListItem, {
  CreatorHasCampaignListItemData,
} from '@/components/CreatorHasCampaignListItem.tsx';
import EmptyState from '@/components/EmptyState.tsx';
import SuspenseFallback from '@/components/SuspenseFallback.tsx';
import useCreatorHasCampaignStatusQueryState from '@/hooks/useCreatorHasCampaignStatusQueryState.ts';
import PageTitle from '@/components/PageTitle.tsx';
import useCampaignNameQueryState from '@/hooks/useCampaignNameQueryState.ts';

export const Route = createFileRoute('/_manager/_app/collaborations/')({
  component: Collaborations,
});

function Collaborations() {
  const { t } = useTranslation(['common', 'manager']);
  const [campaignName, setCampaignName] = useCampaignNameQueryState();
  const [status, setStatus] = useCreatorHasCampaignStatusQueryState();

  const { data, loading, fetchMore } = useQuery(
    myCreatorHasCampaignsForManagerDocument,
    {
      variables: {
        input: {
          searchName: campaignName,
          status: status,
        },
        first: 25,
        page: 1,
      },
    },
  );

  const renderItem = useCallback(
    (itemData: CreatorHasCampaignListItemData) => (
      <CreatorHasCampaignListItem
        key={itemData.id}
        data={itemData}
        variant="outlined"
      />
    ),
    [],
  );

  const handleOnCampaignNameChange = useDebouncedCallback(
    async (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      await setCampaignName(event.target.value);
    },
    250,
  );

  const handleOnStatusChange = useCallback(
    async (event: SelectChangeEvent<CreatorHasCampaignStatus[]>) => {
      const selected = event.target.value;
      if (Array.isArray(selected)) {
        await setStatus(selected);
      }
    },
    [setStatus],
  );

  return (
    <Container>
      <Grid container spacing={4}>
        <Grid size={12}>
          <PageTitle>{t('manager:navigation.collaborations')}</PageTitle>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 12,
            md: 6,
            lg: 8,
          }}>
          <TextField
            variant="outlined"
            label={t('manager:creatorHasCampaign.filter.campaignName.label')}
            defaultValue={campaignName}
            fullWidth
            onChange={handleOnCampaignNameChange}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 12,
            md: 6,
            lg: 4,
          }}>
          <FormControl fullWidth>
            <InputLabel id="status-filter-label">
              {t('manager:creatorHasCampaign.filter.status.label')}
            </InputLabel>
            <CreatorHasCampaignStatusSelect<CreatorHasCampaignStatus[]>
              variant="outlined"
              labelId="status-filter-label"
              label={t('manager:creatorHasCampaign.filter.status.label')}
              value={status}
              multiple
              onChange={handleOnStatusChange}
            />
          </FormControl>
        </Grid>
        <Grid size={12}>
          {data ? (
            <StackList<CreatorHasCampaignListItemData>
              spacing={4}
              loading={loading}
              paginator={data.myCreatorHasCampaigns}
              fetchMore={fetchMore}
              renderItem={renderItem}
              ListEmptyComponent={
                <Card variant="outlined">
                  <CardContent>
                    <EmptyState
                      icon={faHandshake}
                      text={t('manager:creatorHasCampaign.list.empty')}
                    />
                  </CardContent>
                </Card>
              }
            />
          ) : (
            <SuspenseFallback minHeight={300} />
          )}
        </Grid>
      </Grid>
    </Container>
  );
}
